/*
* MockBookViewr, BookViewer에서 사용하는 학습기록 컴포넌트
* 학습기록 API를 사용하여 데이터를 조회합니다.
*/

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { learningRecordService, LearningRecordData } from '../../services/learningRecordService';
import { sharedFolderService, SharedFolderData, isDuplicateSharedFolderItemError } from '../../services/sharedFolderService';
import { getSharedFolderAnnotationVisibility, setSharedFolderAnnotationVisibility } from '../../utils/sharedFolderAnnotationStorage';
import { useFrontAuthStore } from '../../auth/store/authStore';
import { annotationService } from '../../services/annotationService';
import apiClient from '../../../core/http/axios';
import env from '../../../core/config/env';
import { getAuthTokenCookie } from '../../../core/utils/cookie';
import { useSyncSocketContext } from '../../contexts/SyncSocketContext';
import { useLearningRecordsCache, LearningRecordDisplay } from '../../contexts/LearningRecordsCacheContext';

const Panel = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 5px solid transparent;
  background:
    linear-gradient(90deg, #DDD0FF 0%, #E6EAD8 32%, #D3FFD8 66%, #CADCFF 100%) border-box;
  box-shadow: ${props => props.theme.shadows.xl};
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
  color: var(--text-primary);
`;

const PanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
  background-color: var(--bg-secondary);
  overflow: hidden;
`;

const PanelScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  padding: ${props => props.theme.spacing['2xl']};
  padding-top: ${props => props.theme.spacing.md};
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  background-color: var(--bg-secondary);
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const PanelTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.book.color2b};
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.7;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ToggleButton = styled.button`
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.7;
  }

  html.dark-mode & svg {
    filter: invert(1);
  }
`;

const SharedFolderAnnotationToggleButton = styled.button<{ $isVisible: boolean }>`
  position: relative;
  height: 30px;
  width: 68px;
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  border: none;
  cursor: pointer;
  padding: 0;
  background: transparent;
  margin-left: ${props => props.theme.spacing.sm};
  flex-shrink: 0;
`;

const ToggleBackground = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.theme.borderRadius.full};
  transition: background 0.3s ease;
  background: ${props => 
    props.$isVisible 
      ? 'linear-gradient(to bottom, #4169E1, #1E3A8A)' 
      : '#DBDBDB'
  };
`;

const ToggleSlider = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 2.5px;
  height: 25px;
  width: 38px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  left: ${props => props.$isVisible ? '27.5px' : '2.5px'};
`;

const DeleteButton = styled.button<{ $disabled?: boolean }>`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${props => props.$disabled ? props.theme.colors.border.de : props.theme.colors.white};
  color: ${props => props.theme.colors.book.color4b};
  border: 1px solid ${props => props.theme.colors.border.de};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background-color: ${props => props.$disabled ? props.theme.colors.border.be : props.theme.colors.border.f9};
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;


const RecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const RecordItem = styled.div<{ $color?: string; $isDragging?: boolean; $isDragOver?: boolean }>`
  position: relative;
  display: flex;
  min-width: 0;
  width: 100%;
  padding: 10px;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  width: 100%;
  background-color: var(--bg-tertiary);
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  border-left: 2px solid ${props => props.$color || 'transparent'};
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  transition: opacity 0.2s ease;
  ${props => props.$isDragOver ? `
    border: 2px dashed #53E1EE;
    background-color: ${props.theme.colors.gray[50]};
  ` : ''}

  html.dark-mode & {
    min-width: 0;
    width: 100%;
    padding: 10px;
    align-items: flex-start;
    align-content: flex-start;
    gap: 4px;
    align-self: stretch;
    flex-wrap: wrap;
    border-radius: 4px;
    border-left: 2px solid ${props => props.$color || 'transparent'};
    background: #1B1B1B;
  }
`;

const RecordHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const RecordIcon = styled.div<{ $color?: string }>`
  display: flex;
  padding: 2px 6px;
  justify-content: center;
  width: 34px;
  height: 18px;
  border-radius: 10px;
  background-color: var(--bg-secondary);  
  color: ${props => props.theme.colors.book.color4b};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.normal};
  flex-shrink: 0;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const RecordTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const RecordTitle = styled.p`
  color: var(--text-primary);
  font-weight: ${props => props.theme.fontWeight.bold};
  font-size: ${props => props.theme.fontSize.base};
  transition: color 0.3s ease;
  flex: 1;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const RecordMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.xs};
  color: var(--text-secondary);
  padding-left: 0;
`;

const RecordMetaRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.xs};
  color: var(--text-secondary);
  padding-left: 0;
`;

const PageNumber = styled.span`
  color: var(--text-secondary);
`;

const RecordDate = styled.span`
  color: var(--text-secondary);
`;

const TagList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const TagItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: flex-start;
`;

const TagBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 18px;
  border-radius: 10px;
  background-color: var(--bg-secondary);  
  color: ${props => props.theme.colors.book.color4b};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.normal};
  flex-shrink: 0;
  letter-spacing: 0.5px;
`;

const TagContent = styled.span`
  color: ${props => props.theme.colors.book.color6b};
  font-size: ${props => props.theme.fontSize.sm};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const RecordContent = styled.div<{ $isExpanded?: boolean }>`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  line-height: 1.4;
  ${props => props.$isExpanded ? `
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
  ` : `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const MetaSeparator = styled.span`
  color: var(--text-secondary);
  margin: 0 2px;
`;

const GotoLocationButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.book.color4b};
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: opacity 0.2s ease, background-color 0.2s ease;
  align-self: flex-end;
  outline: none;
  &:hover {
    background-color: var(--bg-hover);
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SummaryLabel = styled.span`
  font-weight: ${props => props.theme.fontWeight.medium};
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const LoadingMessage = styled.div`
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  color: var(--text-secondary);
  font-size: ${props => props.theme.fontSize.sm};
`;

const ErrorMessage = styled.div`
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  color: var(--error-color, #ef4444);
  font-size: ${props => props.theme.fontSize.sm};
`;

const EmptyMessage = styled.div`
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  color: var(--text-secondary);
  font-size: ${props => props.theme.fontSize.sm};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ModalContent = styled.div`
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing['2xl']};
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
  border: 1px solid var(--modal-border);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.7;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  color: var(--text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalSectionLabel = styled.div`
  font-weight: ${props => props.theme.fontWeight.semibold};
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ModalSectionContent = styled.div`
  color: var(--text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid var(--modal-border);
`;

const ModalButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background-color: ${props => props.theme.colors.primary[500]};
    color: white;
    
    &:hover {
      background-color: ${props => props.theme.colors.primary[600]};
    }
  }
  
  &.secondary {
    background-color: var(--bg-tertiary);
    color: var(--text-primary);
    
    &:hover {
      background-color: var(--bg-hover);
    }
  }
`;

// 공유 폴더 상세 모달 (학습기록 패널과 같은 크기, 바로 옆에 배치)
const GAP_BETWEEN_PANELS = 12;

const FolderDetailModalContent = styled.div<{
  $left: number;
  $top: number;
  $width: number;
  $height: number;
  $isDraggingItem?: boolean;
  $isDraggingRecordOver?: boolean;
}>`
  position: fixed;
  left: ${props => props.$left}px;
  top: ${props => props.$top}px;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  border-radius: ${props => props.theme.borderRadius.md};
  background:
    linear-gradient(90deg, #DDD0FF 0%, #E6EAD8 32%, #D3FFD8 66%, #CADCFF 100%) border-box;
  box-shadow: ${props => props.theme.shadows.xl};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.xl};
  border: 5px ${props =>
    props.$isDraggingItem ? 'dashed #ef4444' :
    props.$isDraggingRecordOver ? 'dashed #3b82f6' :
    'solid transparent'};
  display: flex;
  flex-direction: column;
  z-index: 1000;
  color: var(--text-primary);
  transition: border-color 0.2s ease, border-style 0.2s ease;
`;

const FolderItemDragHint = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  font-size: 11px;
  font-weight: 500;
  border-top: 1px dashed #ef4444;
`;

const FolderItem = styled.div<{ $isDragOver?: boolean }>`
  display: flex;
  min-width: 0;
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  border-radius: ${props => props.theme.borderRadius.md};
  border-left: 2px solid #25E921;
  background: #F1F9F0;
  position: relative;
  cursor: pointer;
  ${props => props.$isDragOver ? `
    border: 2px dashed #53E1EE;
    background-color: ${props.theme.colors.gray[50]};
  ` : ''}
  transition: border 0.2s ease, background-color 0.2s ease;

  html.dark-mode & {
    min-width: 0;
    width: 100%;
    padding: 10px;
    align-items: flex-start;
    align-content: flex-start;
    gap: 4px;
    align-self: stretch;
    flex-wrap: wrap;
    border-radius: 4px;
    border-left: 2px solid #25E921;
    background: #1B1B1B;
  }
  html.dark-mode & svg {
    filter: brightness(0) invert(1);
  }
`;

const FolderIcon = styled.div`
  display: flex;
  padding: 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 10px;
  font-weight: bold;
  font-family: system-ui, -apple-system, sans-serif;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const FolderItemCountRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  margin-top: ${props => props.theme.spacing.xs};
`;

const FolderDetailCountRow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.xs};
  color: var(--text-secondary);
  background-color: var(--bg-secondary);
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing['2xl']};
  line-height: 1;
`;

const FolderTitle = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.book.color3b};
  font-weight: ${props => props.theme.fontWeight.semibold};
  font-size: ${props => props.theme.fontSize.lg};
  transition: color 0.3s ease;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const FolderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.xs};
  width: 100%;
`;

const UpdateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: #ef4444;
  color: white;
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.semibold};
  flex-shrink: 0;
`;

const SendButton = styled.button<{ $disabled?: boolean }>`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${props => props.$disabled ? props.theme.colors.border.de : props.theme.colors.white};
  color: ${props => props.theme.colors.book.color4b};
  border: 1px solid ${props => props.theme.colors.border.de};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background-color: ${props => props.$disabled ? props.theme.colors.border.be : props.theme.colors.border.f9};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ReceivedFolderSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid var(--modal-border);
`;

const ReceivedFolderGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const ReceivedFolderTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: var(--text-secondary);
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.primary[500]};
  }
`;

const ReceivedFolderItem = styled.div`
  display: flex;
  min-width: 0;
  width: 100%;
  padding: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  border-radius: 4px;
  border-left: 2px solid #53E1EE;
  background: #F4F4F4;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--bg-hover);
  }

  html.dark-mode & {
    min-width: 0;
    width: 100%;
    padding: 10px;
    align-items: flex-start;
    align-content: flex-start;
    gap: 4px;
    align-self: stretch;
    flex-wrap: wrap;
    border-radius: 4px;
    border-left: 2px solid #9B9B9B;
    background: #1B1B1B;
  }
`;

const AudioPlayerContainer = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const PlayButton = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
  
  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const AudioPlayer = styled.audio`
  flex: 1;
  height: 32px;
  background-color: transparent;
  
  &::-webkit-media-controls-panel {
    background-color: transparent;
  }
`;

// 오디오 플레이어 아이템 컴포넌트
interface AudioPlayerItemProps {
  recordUuid: string;
  fileUuid: string;
  playingAudioUuid: string | null;
  setPlayingAudioUuid: (uuid: string | null) => void;
  audioRefs: React.MutableRefObject<Map<string, HTMLAudioElement>>;
}

const AudioPlayerItem: React.FC<AudioPlayerItemProps> = ({
  recordUuid,
  fileUuid,
  playingAudioUuid,
  setPlayingAudioUuid,
  audioRefs,
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 오디오 파일 로드
  useEffect(() => {
    if (!fileUuid) return;

    setIsLoading(true);
    setError(null);

    const loadAudio = async () => {
      try {
        const token = getAuthTokenCookie();
        const apiUrl = env.REACT_APP_API_URL || '/api';
        const url = `${apiUrl}/viewer/files/${fileUuid}/download`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`오디오 파일 로드 실패: ${response.status}`);
        }

        // Content-Type 확인
        const contentType = response.headers.get('Content-Type') || 'audio/mpeg';
        // console.log('[AudioPlayerItem] Content-Type:', contentType);
        
        const blob = await response.blob();
        
        // Blob에 올바른 MIME 타입 설정 (응답의 Content-Type이 없거나 잘못된 경우 대비)
        let mimeType = contentType;
        if (!mimeType || mimeType === 'application/octet-stream') {
          // 기본값으로 audio/mpeg 사용 (MP3)
          mimeType = 'audio/mpeg';
        }
        
        // Blob을 올바른 MIME 타입으로 재생성
        const typedBlob = new Blob([blob], { type: mimeType });
        const blobUrl = URL.createObjectURL(typedBlob);
        
        // console.log('[AudioPlayerItem] Blob URL 생성:', blobUrl, 'MIME type:', mimeType);
        setAudioUrl(blobUrl);
      } catch (err: any) {
        // console.error('[AudioPlayerItem] 오디오 파일 로드 실패:', err);
        setError(err.message || '오디오 파일을 불러올 수 없습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAudio();

    // 정리 함수
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [fileUuid]);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audioElement = audioRefs.current.get(recordUuid);

    if (audioElement && audioUrl) {
      if (playingAudioUuid === recordUuid) {
        // 현재 재생 중이면 일시정지
        audioElement.pause();
        setPlayingAudioUuid(null);
      } else {
        // 다른 오디오가 재생 중이면 정지
        if (playingAudioUuid) {
          const prevAudio = audioRefs.current.get(playingAudioUuid);
          if (prevAudio) {
            prevAudio.pause();
            prevAudio.currentTime = 0;
          }
        }
        // 새 오디오 재생
        audioElement.play().catch((err) => {
          // console.error('[AudioPlayerItem] 재생 실패:', err);
          setError('오디오 재생에 실패했습니다.');
        });
        setPlayingAudioUuid(recordUuid);
      }
    }
  };

  if (error) {
    return (
      <AudioPlayerContainer>
        <span style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.875rem' }}>
          {error}
        </span>
      </AudioPlayerContainer>
    );
  }

  if (isLoading || !audioUrl) {
    return (
      <AudioPlayerContainer>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          오디오 로딩 중...
        </span>
      </AudioPlayerContainer>
    );
  }

  return (
    <AudioPlayerContainer>
      <PlayButton
        onClick={handlePlayClick}
        disabled={!audioUrl}
      >
        {playingAudioUuid === recordUuid ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M10.416 7.45096C11.2222 8.10871 11.625 8.43721 11.625 8.99971C11.625 9.56221 11.2222 9.89071 10.416 10.5485C10.1932 10.73 9.97275 10.901 9.7695 11.0435C9.59175 11.1687 9.39 11.2977 9.1815 11.4245C8.37675 11.9135 7.97475 12.158 7.614 11.8872C7.254 11.6172 7.221 11.0502 7.155 9.91621C7.137 9.59596 7.125 9.28171 7.125 8.99971C7.125 8.71771 7.137 8.40346 7.155 8.08321C7.221 6.94921 7.254 6.38296 7.614 6.11221C7.97475 5.84146 8.3775 6.08596 9.1815 6.57496C9.39 6.70171 9.59175 6.83071 9.7695 6.95596C9.972 7.09846 10.1932 7.26946 10.416 7.45096Z" stroke="#4B4B4B" strokeWidth="1.13"/>
            <path d="M5.25 2.50351C6.38959 1.84405 7.68336 1.49784 9 1.50001C13.1422 1.50001 16.5 4.85776 16.5 9.00001C16.5 13.1423 13.1422 16.5 9 16.5C4.85775 16.5 1.5 13.1423 1.5 9.00001C1.5 7.63426 1.86525 6.35251 2.5035 5.25001" stroke="#4B4B4B" strokeWidth="1.13" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M11.9168 6.50016C11.9168 9.05358 11.9168 10.3303 11.1233 11.1233C10.3308 11.9168 9.05358 11.9168 6.50016 11.9168C3.94675 11.9168 2.67004 11.9168 1.8765 11.1233C1.0835 10.3308 1.0835 9.05358 1.0835 6.50016C1.0835 3.94675 1.0835 2.67004 1.8765 1.8765C2.67058 1.0835 3.94675 1.0835 6.50016 1.0835C9.05358 1.0835 10.3303 1.0835 11.1233 1.8765C11.6509 2.40408 11.8275 3.14562 11.887 4.3335" stroke="#EBEBEB" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        )}
      </PlayButton>
      <AudioPlayer
        ref={(audio) => {
          if (audio) {
            audioRefs.current.set(recordUuid, audio);
          } else {
            audioRefs.current.delete(recordUuid);
          }
        }}
        src={audioUrl}
        onEnded={() => {
          setPlayingAudioUuid(null);
        }}
        onPause={() => {
          if (playingAudioUuid === recordUuid) {
            setPlayingAudioUuid(null);
          }
        }}
        controls
      />
    </AudioPlayerContainer>
  );
};

interface LearningRecordTag {
  label: string;
  content: string;
}

interface LearningRecord {
  title: string;
  date: string;
  tags: LearningRecordTag[];
  pageNo: number | null;
  pageDisplayNumber: number | null;
  pageIdx: number | null;
  recordType: string;
  color: string;
  iconLabel: string;
  summary: string;
  annotationType?: string;
  subject?: string;
}

interface PendingRecordingCard {
  title: string;
  audioBlobUrl: string;
  createdAt: string;
}

interface LearningRecordsPanelProps {
  onToggle: () => void;
  bookUuid?: string; // bookUuid를 props로 받을 수 있도록 추가
  onRecordClick?: (record: LearningRecordData) => void; // 학습 기록 클릭 핸들러 (DIALOG 타입용)
  onPageClick?: (pageNo: number, annotationUuid?: string, memoFallback?: { title: string; subject: string }, componentNo?: number, sharedAnnotationData?: { startOffset: number; endOffset: number; selectedText?: string; color?: string; type?: string }) => void; // 페이지 번호 클릭 핸들러 (annotation_uuid 옵션, 메모 fallback, componentNo, 공유 어노테이션 데이터)
  pendingRecordingCard?: PendingRecordingCard | null;
  onPendingRecordingCardClear?: () => void;
  onScrollTopChange?: (scrollTop: number) => void;
  scrollTopRestore?: number;
  restoreFolderUuid?: string | null; // 되돌아가기 시 공유폴더 모달 복원용
  onRestoreFolderConsumed?: () => void; // 복원 완료 후 초기화 콜백
}

// record_type별 색상 및 아이콘 설정
const getRecordTypeConfig = (recordType: string): { color: string; iconLabel: string; label: string } => {
  switch (recordType) {
    case 'AUDIO':
      return { color: '#DA0D0D', iconLabel: '녹음', label: '녹음' };
    case 'ANNOTATION':
      return { color: '#FFBF00', iconLabel: '마킹', label: '마킹' };
    case 'NOTE':
      return { color: '#091EBB', iconLabel: '메모', label: '메모' };
    case 'DIALOG':
    case 'QUIZ':
    case 'CHAT':
      return { color: '#B10DDA', iconLabel: 'AI', label: '채팅' };
    default:
      return { color: '#6b7280', iconLabel: '파일', label: '기타' };
  }
};

const MEMO_CONTENT_MAX_LEN = 250;

const truncateWithEllipsis = (text: string, maxLen: number): string => {
  if (!text || text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
};

// 날짜 포맷팅 (YYYY.MM.DD)
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}.${month}.${day}`;
  } catch (error) {
    // console.error('[LearningRecordsPanel] 날짜 포맷 실패:', error);
    return '';
  }
};

// shared_folder_item.item_type CHECK에 DIALOG가 없으면 DB INSERT가 실패함. 백엔드 CK에 DIALOG 추가 시 이 제한 제거 가능.
const isRecordTypeAllowedInSharedFolder = (recordType: string | undefined) =>
  recordType !== 'DIALOG';

const LearningRecordsPanel: React.FC<LearningRecordsPanelProps> = ({ onToggle, bookUuid, onRecordClick, onPageClick, pendingRecordingCard, onPendingRecordingCardClear, onScrollTopChange, scrollTopRestore, restoreFolderUuid, onRestoreFolderConsumed }) => {
  const { user } = useFrontAuthStore();
  const isProfessor = user?.role === 'professor';
  const isStudent = user?.role === 'student';
  
  // 동기화 소켓 컨텍스트에서 공유 폴더 관련 함수/상태 가져오기
  const { 
    isSyncSocketConnected, 
    sendSharedFolderPublish, 
    receivedSharedFolder, 
    clearReceivedSharedFolder 
  } = useSyncSocketContext();

  const { bookUid } = useParams<{ bookUid: string }>();
  const effectiveBookUuid = bookUuid ?? bookUid ?? '';

  const cacheContext = useLearningRecordsCache();
  const getCache = cacheContext?.getCache;
  const preload = cacheContext?.preload;
  const invalidate = cacheContext?.invalidate;
  const loadMore = cacheContext?.loadMore;
  const addToSharedFolderUuids = cacheContext?.addToSharedFolderUuids;
  const removeFromSharedFolderUuids = cacheContext?.removeFromSharedFolderUuids;
  
  const panelRef = useRef<HTMLDivElement>(null);
  const panelContainerRef = useRef<HTMLDivElement>(null);
  const [frozenScrollTopRestore] = useState(() => scrollTopRestore ?? 0);
  const scrollRestoreDoneRef = useRef(false);

  // 캐시에서 데이터 파생 (뷰어 로드 시 preload로 채워짐)
  const cache = effectiveBookUuid && getCache ? getCache(effectiveBookUuid) : null;
  const filteredRecordsWithOriginalData = cache
    ? cache.recordsWithOriginalData.filter((i) => !cache.sharedFolderRecordUuids.has(i.originalData.record_uuid))
    : [];
  const records = filteredRecordsWithOriginalData.map((i) => i.record);
  const recordsWithOriginalData = filteredRecordsWithOriginalData;
  const folders = cache?.folders ?? [];
  const sharedFolderRecordUuids = cache?.sharedFolderRecordUuids ?? new Set();
  const foldersLoaded = !!cache;

  // 생성일 기준으로 공유 폴더와 학습 기록을 병합·정렬
  const mergedItems = React.useMemo(() => {
    type ListItem = { type: 'folder'; folder: SharedFolderData } | { type: 'record'; record: LearningRecordDisplay; originalData: LearningRecordData; index: number };
    const folderItems: ListItem[] = folders.map((f) => ({ type: 'folder' as const, folder: f }));
    const recordItems: ListItem[] = recordsWithOriginalData.map((item, index) => ({
      type: 'record' as const,
      record: item.record,
      originalData: item.originalData,
      index,
    }));
    const items: ListItem[] = [...folderItems, ...recordItems];
    items.sort((a, b) => {
      const getCreatedAt = (x: ListItem) => (x.type === 'folder' ? x.folder.created_at : x.originalData.created_at);
      return new Date(getCreatedAt(b)).getTime() - new Date(getCreatedAt(a)).getTime();
    });
    return items;
  }, [folders, recordsWithOriginalData]);
  const uuidsLoaded = !!cache;
  const loading = cache?.loading ?? (cache === null && effectiveBookUuid ? true : false);
  const loadingMore = cache?.loadingMore ?? false;
  const error = cache?.error ?? null;
  const total = cache?.total ?? 0;
  const hasMore = cache?.hasMore ?? false;

  const preloadTriggeredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!effectiveBookUuid || !preload) return;
    if (preloadTriggeredRef.current.has(effectiveBookUuid) || cache) return;
    preloadTriggeredRef.current.add(effectiveBookUuid);
    preload(effectiveBookUuid);
  }, [effectiveBookUuid, preload, cache]);

  // 드래그 앤 드롭 상태
  const [draggedRecordUuid, setDraggedRecordUuid] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<{ type: 'record' | 'folder'; uuid: string } | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [draggedFolderItem, setDraggedFolderItem] = useState<{ itemNo: number; annotation: any } | null>(null);
  const [isDraggingRecordOverModal, setIsDraggingRecordOverModal] = useState(false);
  const folderDetailModalRef = useRef<HTMLDivElement>(null);
  const folderItemDroppedInsideRef = useRef(false);
  const dragOverModalCountRef = useRef(0);
  
  // 공유 폴더 상세 모달 상태 (학습 기록과 독립적으로 표시)
  const [folderDetailModal, setFolderDetailModal] = useState<{
    folder: SharedFolderData;
    annotations: any[];
    annotationVisible: boolean;
  } | null>(null);
  const [folderModalBounds, setFolderModalBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [loadingFolderAnnotations, setLoadingFolderAnnotations] = useState(false);
  const [folderItemCounts, setFolderItemCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (folders.length === 0) {
      setFolderItemCounts({});
      return;
    }
    const fetchCounts = async () => {
      const counts: Record<string, number> = {};
      await Promise.all(
        folders.map(async (f) => {
          try {
            const res = await sharedFolderService.getFolderItems(f.folder_uuid, 1, 0);
            counts[f.folder_uuid] = res.total;
          } catch {
            counts[f.folder_uuid] = 0;
          }
        })
      );
      setFolderItemCounts(counts);
    };
    fetchCounts();
  }, [folders]);

  // 음성 재생 상태
  const [playingAudioUuid, setPlayingAudioUuid] = useState<string | null>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  // 메모/녹음 카드 펼침 상태 (UUID Set)
  const [expandedRecordUuids, setExpandedRecordUuids] = useState<Set<string>>(new Set());
  const [expandedFolderItemUuids, setExpandedFolderItemUuids] = useState<Set<string>>(new Set());
  const toggleRecordExpand = useCallback((uuid: string) => {
    setExpandedRecordUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);
  const toggleFolderItemExpand = useCallback((uuid: string) => {
    setExpandedFolderItemUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);
  
  const reloadLearningRecords = useCallback(() => {
    if (effectiveBookUuid && invalidate) invalidate(effectiveBookUuid);
  }, [effectiveBookUuid, invalidate]);

  // pending card가 있을 때 새 AUDIO 레코드 등장 시 자동 제거
  const audioRecordUuidsSnapshotRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!pendingRecordingCard) {
      audioRecordUuidsSnapshotRef.current = null;
      return;
    }
    const currentAudioUuids = new Set(
      recordsWithOriginalData
        .filter(item => item.record.recordType === 'AUDIO')
        .map(item => item.originalData.record_uuid)
    );
    if (audioRecordUuidsSnapshotRef.current === null) {
      audioRecordUuidsSnapshotRef.current = currentAudioUuids;
      return;
    }
    const hasNewAudio = [...currentAudioUuids].some(uuid => !audioRecordUuidsSnapshotRef.current!.has(uuid));
    if (hasNewAudio) {
      onPendingRecordingCardClear?.();
    }
  }, [recordsWithOriginalData, pendingRecordingCard, onPendingRecordingCardClear]);

  // pending card용 오디오 플레이어 상태
  const [pendingAudioPlaying, setPendingAudioPlaying] = useState(false);
  const pendingAudioRef = useRef<HTMLAudioElement | null>(null);

  const togglePendingAudio = useCallback(() => {
    if (!pendingRecordingCard?.audioBlobUrl) return;
    if (pendingAudioPlaying && pendingAudioRef.current) {
      pendingAudioRef.current.pause();
      pendingAudioRef.current.currentTime = 0;
      setPendingAudioPlaying(false);
    } else {
      const audio = new Audio(pendingRecordingCard.audioBlobUrl);
      pendingAudioRef.current = audio;
      audio.onended = () => setPendingAudioPlaying(false);
      audio.play();
      setPendingAudioPlaying(true);
    }
  }, [pendingRecordingCard, pendingAudioPlaying]);

  useEffect(() => {
    if (!pendingRecordingCard) {
      if (pendingAudioRef.current) {
        pendingAudioRef.current.pause();
        pendingAudioRef.current = null;
      }
      setPendingAudioPlaying(false);
    }
  }, [pendingRecordingCard]);

  // 스크롤 핸들러
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const st = target.scrollTop;
    onScrollTopChange?.(st);
    if (!scrollRestoreDoneRef.current && frozenScrollTopRestore > 0) {
      const maxScroll = Math.max(0, target.scrollHeight - target.clientHeight);
      const expected = Math.min(frozenScrollTopRestore, maxScroll);
      if (Math.abs(st - expected) > 8) scrollRestoreDoneRef.current = true;
    }
    const scrollBottom = target.scrollHeight - st - target.clientHeight;
    if (scrollBottom < 100 && hasMore && !loadingMore && !loading && effectiveBookUuid && loadMore) {
      loadMore(effectiveBookUuid);
    }
  }, [hasMore, loadingMore, loading, effectiveBookUuid, loadMore, onScrollTopChange, frozenScrollTopRestore]);

  useLayoutEffect(() => {
    if (frozenScrollTopRestore <= 0 || scrollRestoreDoneRef.current) return;
    const el = panelRef.current;
    if (!el || loading) return;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const next = Math.min(frozenScrollTopRestore, maxScroll);
    el.scrollTop = next;
    if (next >= frozenScrollTopRestore - 1) scrollRestoreDoneRef.current = true;
  }, [frozenScrollTopRestore, loading, loadingMore, mergedItems.length]);

  // 컴포넌트 언마운트 시 오디오 정리 및 스크롤 인터벌 정리
  useEffect(() => {
    return () => {
      // 모든 오디오 정지
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      audioRefs.current.clear();
      setPlayingAudioUuid(null);
      
      // 스크롤 인터벌 정리
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, []);

  // 공유 폴더 수락 시 캐시 갱신
  useEffect(() => {
    if (isStudent && receivedSharedFolder?.folder_uuid && effectiveBookUuid && invalidate) {
      const timer = setTimeout(() => invalidate(effectiveBookUuid), 500);
      return () => clearTimeout(timer);
    }
  }, [isStudent, receivedSharedFolder, effectiveBookUuid, invalidate]);

  // 공유 폴더 목록이 로드되면 UUID 수집 (학습 기록 로드 전에 완료되어야 함)
  // 하지만 초기 로드 시에는 학습 기록 로드 함수 내에서 직접 수집하므로 여기서는 제거
  // useEffect(() => {
  //   if (folders.length > 0) {
  //     loadSharedFolderRecordUuids();
  //   } else {
  //     setSharedFolderRecordUuids(new Set());
  //   }
  // }, [folders, loadSharedFolderRecordUuids]);

  // sharedFolderRecordUuids 변경 시 재필터링 제거
  // (초기 로드 시 UUID 수집 완료 후 필터링하므로 재필터링 불필요)

  // 드래그 시작 핸들러
  const handleDragStart = useCallback((e: React.DragEvent, recordUuid: string) => {
    if (!isProfessor) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', recordUuid);
    setDraggedRecordUuid(recordUuid);
  }, [isProfessor]);

  // 자동 스크롤 핸들러 (드래그 중)
  const handleDragOverWithAutoScroll = useCallback((e: React.DragEvent) => {
    if (!isProfessor || !draggedRecordUuid || !panelRef.current) return;
    
    const panel = panelRef.current;
    const rect = panel.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50; // 상단/하단 경계에서 스크롤 시작 거리 (px)
    const scrollSpeed = 10; // 스크롤 속도 (px)
    
    // 기존 스크롤 인터벌 정리
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    
    // 상단 경계 근처에서 위로 스크롤
    if (mouseY - rect.top < scrollThreshold && panel.scrollTop > 0) {
      scrollIntervalRef.current = setInterval(() => {
        if (panel.scrollTop > 0) {
          panel.scrollTop = Math.max(0, panel.scrollTop - scrollSpeed);
        } else {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
      }, 16); // 약 60fps
    }
    // 하단 경계 근처에서 아래로 스크롤
    else if (rect.bottom - mouseY < scrollThreshold && panel.scrollTop < panel.scrollHeight - panel.clientHeight) {
      scrollIntervalRef.current = setInterval(() => {
        if (panel.scrollTop < panel.scrollHeight - panel.clientHeight) {
          panel.scrollTop = Math.min(panel.scrollHeight - panel.clientHeight, panel.scrollTop + scrollSpeed);
        } else {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
      }, 16); // 약 60fps
    }
  }, [isProfessor, draggedRecordUuid]);

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    // 자동 스크롤 인터벌 정리
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setDraggedRecordUuid(null);
    setDragOverTarget(null);
    setIsDraggingRecordOverModal(false);
    dragOverModalCountRef.current = 0;
  }, []);

  // 드래그 오버 핸들러 (카드뷰)
  const handleDragOverRecord = useCallback((e: React.DragEvent, targetRecordUuid: string) => {
    if (!isProfessor || !draggedRecordUuid) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'record', uuid: targetRecordUuid });
    // 자동 스크롤 처리
    handleDragOverWithAutoScroll(e);
  }, [isProfessor, draggedRecordUuid, handleDragOverWithAutoScroll]);

  // 드래그 오버 핸들러 (디렉토리)
  const handleDragOverFolder = useCallback((e: React.DragEvent, folderUuid: string) => {
    if (!isProfessor || !draggedRecordUuid) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'folder', uuid: folderUuid });
    // 자동 스크롤 처리
    handleDragOverWithAutoScroll(e);
  }, [isProfessor, draggedRecordUuid, handleDragOverWithAutoScroll]);

  // 드래그 리브 핸들러
  const handleDragLeave = useCallback(() => {
    setDragOverTarget(null);
  }, []);

  // 드롭 핸들러 (디렉토리 위에 드롭 - 카드뷰를 디렉토리로 이동)
  const handleDropOnFolder = useCallback(async (e: React.DragEvent, folderUuid: string) => {
    if (!isProfessor || !draggedRecordUuid || !effectiveBookUuid) return;
    e.preventDefault();
    e.stopPropagation();
    
    const sourceRecordUuid = draggedRecordUuid;
    
    try {
      // 드래그된 카드 정보 찾기
      const sourceRecord = recordsWithOriginalData.find(
        item => item.originalData.record_uuid === sourceRecordUuid
      );
      
      if (!sourceRecord) {
        // console.warn('[LearningRecordsPanel] 소스 카드를 찾을 수 없습니다.');
        return;
      }

      if (!isRecordTypeAllowedInSharedFolder(sourceRecord.originalData.record_type)) {
        alert('대화형 학습 기록은 공유 폴더에 담을 수 없습니다.');
        return;
      }

      // record_uuid 확인
      const recordUuid = sourceRecord.originalData.record_uuid;
      
      // 폴더에 아이템 추가
      const result = await sharedFolderService.addItemToFolder(
        folderUuid,
        recordUuid,
        0,
        {}
      );

      if (result.success) {
        addToSharedFolderUuids?.(effectiveBookUuid, recordUuid);
        setFolderItemCounts((prev) => ({
          ...prev,
          [folderUuid]: (prev[folderUuid] ?? 0) + 1,
        }));
      } else if (isDuplicateSharedFolderItemError(result.error || '')) {
        addToSharedFolderUuids?.(effectiveBookUuid, recordUuid);
      } else {
        alert(`디렉토리로 이동에 실패했습니다.${result.error ? `\n${result.error}` : ''}`);
      }
    } catch (err) {
      // console.error('[LearningRecordsPanel] 디렉토리로 이동 실패:', err);
      alert(`디렉토리로 이동에 실패했습니다.${err instanceof Error ? `\n${err.message}` : ''}`);
    } finally {
      setDragOverTarget(null);
      setDraggedRecordUuid(null);
    }
  }, [isProfessor, draggedRecordUuid, effectiveBookUuid, recordsWithOriginalData, addToSharedFolderUuids]);

  // 드롭 핸들러 (카드뷰 위에 드롭 - 디렉토리 생성)
  const handleDropOnRecord = useCallback(async (e: React.DragEvent, targetRecordUuid: string) => {
    if (!isProfessor || !draggedRecordUuid || !effectiveBookUuid) return;
    e.preventDefault();
    e.stopPropagation();
    
    const sourceRecordUuid = draggedRecordUuid;
    
    // 같은 카드에 드롭한 경우 무시
    if (sourceRecordUuid === targetRecordUuid) {
      setDragOverTarget(null);
      setDraggedRecordUuid(null);
      return;
    }

    try {
      // 드래그된 카드와 타겟 카드 정보 찾기
      const sourceRecord = recordsWithOriginalData.find(
        item => item.originalData.record_uuid === sourceRecordUuid
      );
      const targetRecord = recordsWithOriginalData.find(
        item => item.originalData.record_uuid === targetRecordUuid
      );

      if (!sourceRecord) {
        return;
      }
      if (!isRecordTypeAllowedInSharedFolder(sourceRecord.originalData.record_type)) {
        alert('대화형 학습 기록은 공유 폴더에 담을 수 없습니다.');
        return;
      }
      if (!targetRecord) {
        return;
      }
      if (!isRecordTypeAllowedInSharedFolder(targetRecord.originalData.record_type)) {
        alert('대화형 학습 기록은 공유 폴더에 담을 수 없습니다.');
        return;
      }
      
      // 폴더 제목 생성
      const folderTitle = `폴더: ${targetRecord.record.title}`;
      
      // 폴더 설명 생성
      const folderDescription = sourceRecord && targetRecord
        ? `${sourceRecord.record.title}와 ${targetRecord.record.title}를 포함한 폴더`
        : '드래그 앤 드롭으로 생성된 폴더';
      
      // 1. 디렉토리 생성 API 호출 (meta는 빈 객체)
      const newFolder = await sharedFolderService.createSharedFolder(
        effectiveBookUuid,
        folderTitle,
        folderDescription,
        {}
      );

      if (!newFolder) {
        alert('디렉토리 생성에 실패했습니다.');
        return;
      }

      // 2. 폴더에 카드뷰 추가 (소스 카드와 타겟 카드 모두)
      const addCardToFolder = async (
        record: typeof sourceRecord,
        sortOrder: number
      ) => {
        if (!record) return;

        const recordUuid = record.originalData.record_uuid;

        const addResult = await sharedFolderService.addItemToFolder(
          newFolder.folder_uuid,
          recordUuid,
          sortOrder,
          {}
        );
        if (addResult.success || isDuplicateSharedFolderItemError(addResult.error || '')) {
          addToSharedFolderUuids?.(effectiveBookUuid, recordUuid);
        }
      };

      // 소스 카드 추가 (sort_order: 0)
      await addCardToFolder(sourceRecord, 0);

      // 타겟 카드 추가 (sort_order: 1)
      await addCardToFolder(targetRecord, 1);

      invalidate?.(effectiveBookUuid);
    } catch (err) {
      // console.error('[LearningRecordsPanel] 디렉토리 생성 실패:', err);
      alert('디렉토리 생성에 실패했습니다.');
    } finally {
      setDragOverTarget(null);
      setDraggedRecordUuid(null);
    }
  }, [isProfessor, draggedRecordUuid, effectiveBookUuid, recordsWithOriginalData, addToSharedFolderUuids, invalidate]);

  // 폴더 클릭 핸들러 (depth 네비게이션) - 교수자 및 학생 모두 사용
  const handleFolderClick = useCallback(async (folder: SharedFolderData) => {
    // console.log('[LearningRecordsPanel] 폴더 클릭, getFolderItems API 호출:', folder.folder_uuid, folder.title);
    setLoadingFolderAnnotations(true);
    setFolderDetailModal({
      folder,
      annotations: [],
      annotationVisible: getSharedFolderAnnotationVisibility(folder.folder_uuid),
    });
    try {
      const annotations = await sharedFolderService.getFolderAnnotations(folder.folder_uuid);
      
      // annotation API 데이터와 매칭 (일반 학습 기록과 동일한 방식)
      const annotationRecords = annotations.filter(
        (item: any) => item.type && item.annotation_uuid && item.page_no !== null
      );
      
      let annotationMap = new Map<string, any>();
      
      if (annotationRecords.length > 0 && effectiveBookUuid) {
        // page_no 범위 계산
        const pageNos = annotationRecords
          .map((item: any) => item.page_no)
          .filter((pageNo: any) => pageNo !== null && pageNo !== undefined);
        
        if (pageNos.length > 0) {
          const minPageNo = Math.min(...pageNos);
          const maxPageNo = Math.max(...pageNos);
          
          try {
            // annotation API 호출
            const annotationApiResponse = await apiClient.get(
              `/viewer/books/${effectiveBookUuid}/annotations`,
              {
                params: {
                  start_page: minPageNo,
                  end_page: maxPageNo,
                },
                headers: {
                  'accept': 'application/json',
                },
              }
            );
            
            // annotation 데이터를 Map으로 변환
            const annotationsData = annotationApiResponse.data?.data?.annotations || [];
            annotationsData.forEach((item: any) => {
              const annotationItem = item.annotation;
              if (annotationItem && annotationItem.annotation_uuid) {
                annotationMap.set(annotationItem.annotation_uuid, {
                  type: annotationItem.type,
                  data: {
                    title: annotationItem.data?.title,
                    subject: annotationItem.data?.subject,
                    color: annotationItem.data?.color,
                    border_color: annotationItem.data?.border_color,
                  },
                  annotation_uuid: annotationItem.annotation_uuid,
                });
              }
            });
          } catch (annotationErr) {
            // console.warn('[LearningRecordsPanel] 폴더 Annotation API 호출 실패 (계속 진행):', annotationErr);
          }
        }
      }
      
      // annotation API 데이터와 매칭하여 업데이트
      const enrichedAnnotations = annotations.map((item: any) => {
        if (item.annotation_uuid && annotationMap.has(item.annotation_uuid)) {
          const apiData = annotationMap.get(item.annotation_uuid);
          return {
            ...item,
            // annotation API의 데이터로 덮어쓰기 (우선순위)
            type: apiData.type || item.type,
            data: {
              ...item.data,
              title: apiData.data?.title || item.data?.title,
              subject: apiData.data?.subject || item.data?.subject,
              color: apiData.data?.color || item.data?.color,
              border_color: apiData.data?.border_color || item.data?.border_color,
            },
            // annotation API에서 가져온 title을 별도로 저장
            annotationApiTitle: apiData.data?.title,
            annotationApiSubject: apiData.data?.subject,
          };
        }
        return item;
      });
      
      const annotationVisible = getSharedFolderAnnotationVisibility(folder.folder_uuid);
      setFolderDetailModal({
        folder,
        annotations: enrichedAnnotations,
        annotationVisible,
      });

      // BookViewer에 공유 폴더 선택 이벤트 전달
      window.dispatchEvent(new CustomEvent('sharedFolderSelected', {
        detail: { folder }
      }));
    } catch (err) {
      // console.error('[LearningRecordsPanel] 폴더 annotations 로드 실패:', err);
      alert('폴더 내용을 불러오는데 실패했습니다.');
      setFolderDetailModal(null);
    } finally {
      setLoadingFolderAnnotations(false);
    }
  }, [effectiveBookUuid]);

  // 되돌아가기 시 공유폴더 모달 복원
  useEffect(() => {
    if (!restoreFolderUuid || folders.length === 0) return;
    const folder = folders.find(f => f.folder_uuid === restoreFolderUuid);
    if (folder) {
      handleFolderClick(folder);
    }
    onRestoreFolderConsumed?.();
  }, [restoreFolderUuid, folders, handleFolderClick, onRestoreFolderConsumed]);

  // 공유 폴더 모달 닫기 핸들러
  const handleCloseFolderModal = useCallback(() => {
    setFolderDetailModal(null);

    // BookViewer에 공유 폴더 선택 해제 이벤트 전달
    window.dispatchEvent(new CustomEvent('sharedFolderSelected', {
      detail: { folder: null }
    }));
  }, []);

  // 교수의 노트 모달에서 "N개의 학습 기록" 클릭 시 폴더 상세 모달 열기
  useEffect(() => {
    const handleProfessorNotesOpenFolderDetail = (event: CustomEvent) => {
      const folder = event.detail?.folder;
      if (folder && handleFolderClick) {
        handleFolderClick(folder);
      }
    };
    window.addEventListener('professorNotesOpenFolderDetail', handleProfessorNotesOpenFolderDetail as EventListener);
    return () => {
      window.removeEventListener('professorNotesOpenFolderDetail', handleProfessorNotesOpenFolderDetail as EventListener);
    };
  }, [handleFolderClick]);

  // 공유 폴더 표시 여부 변경 이벤트 리스너 (다른 곳에서 변경되었을 때 반영)
  useEffect(() => {
    const handleSharedFolderVisibilityChanged = (event: CustomEvent) => {
      const folderUuid = event.detail.folderUuid;
      if (folderDetailModal?.folder.folder_uuid === folderUuid) {
        const visibility = getSharedFolderAnnotationVisibility(folderUuid);
        setFolderDetailModal(prev => prev ? { ...prev, annotationVisible: visibility } : null);
      }
    };

    window.addEventListener('sharedFolderVisibilityChanged', handleSharedFolderVisibilityChanged as EventListener);

    return () => {
      window.removeEventListener('sharedFolderVisibilityChanged', handleSharedFolderVisibilityChanged as EventListener);
    };
  }, [folderDetailModal?.folder.folder_uuid]);

  // 학습기록 패널 위치 기준으로 공유 폴더 모달을 바로 옆에 배치
  useLayoutEffect(() => {
    if (!folderDetailModal || !panelContainerRef.current) {
      setFolderModalBounds(null);
      return;
    }
    const updateBounds = () => {
      const rect = panelContainerRef.current?.getBoundingClientRect();
      if (rect) {
        setFolderModalBounds({
          left: rect.right + GAP_BETWEEN_PANELS,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    };
    updateBounds();
    const resizeObserver = new ResizeObserver(updateBounds);
    resizeObserver.observe(panelContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [folderDetailModal]);

  // 폴더 상세 모달 열림/닫힘 시 BookViewer에 알림 (삭제 툴바 표시 여부 판단용)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('folderDetailModalChanged', { detail: { open: !!folderDetailModal } }));
  }, [folderDetailModal]);

  // 공유 폴더 어노테이션 토글 핸들러
  const handleToggleSharedFolderAnnotations = useCallback(() => {
    if (!folderDetailModal) return;

    const newVisibility = !folderDetailModal.annotationVisible;

    // localStorage에 저장
    setSharedFolderAnnotationVisibility(folderDetailModal.folder.folder_uuid, newVisibility);

    // state 업데이트
    setFolderDetailModal(prev => prev ? { ...prev, annotationVisible: newVisibility } : null);

    // Custom Event 발생시켜 BookViewer에 알림
    window.dispatchEvent(new CustomEvent('sharedFolderVisibilityChanged', {
      detail: {
        folderUuid: folderDetailModal.folder.folder_uuid,
        isVisible: newVisibility
      }
    }));
  }, [folderDetailModal]);

  // 공유 폴더 삭제 핸들러
  const handleDeleteFolder = useCallback(async () => {
    if (!folderDetailModal || !isProfessor) return;

    if (!window.confirm(`"${folderDetailModal.folder.title}" 폴더를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const success = await sharedFolderService.deleteSharedFolder(folderDetailModal.folder.folder_uuid);
      if (success) {
        invalidate?.(effectiveBookUuid);
        handleCloseFolderModal();
        alert('폴더가 삭제되었습니다.');
      } else {
        alert('폴더 삭제에 실패했습니다.');
      }
    } catch (error) {
      // console.error('[LearningRecordsPanel] 폴더 삭제 오류:', error);
      alert('폴더 삭제 중 오류가 발생했습니다.');
    }
  }, [folderDetailModal, isProfessor, effectiveBookUuid, invalidate, handleCloseFolderModal]);

  // 폴더 아이템 삭제 핸들러
  const handleDeleteFolderItem = useCallback(async (itemNo: number) => {
    if (!folderDetailModal || !isProfessor) return;

    if (!window.confirm('이 항목을 폴더에서 삭제하시겠습니까?')) {
      return;
    }

    try {
      const success = await sharedFolderService.deleteFolderItem(folderDetailModal.folder.folder_uuid, itemNo);
      if (success) {
        // 삭제된 아이템의 record_uuid 찾기
        const deletedItem = folderDetailModal.annotations.find((item: any) => item.item_no === itemNo);
        if (deletedItem && deletedItem.record && deletedItem.record.record_uuid) {
          removeFromSharedFolderUuids?.(effectiveBookUuid, deletedItem.record.record_uuid);
        }
        setFolderItemCounts((prev) => ({
          ...prev,
          [folderDetailModal.folder.folder_uuid]: Math.max(0, (prev[folderDetailModal.folder.folder_uuid] ?? 0) - 1),
        }));
        // 폴더 내용 새로고침
        await handleFolderClick(folderDetailModal.folder);
        alert('항목이 삭제되었습니다.');
      } else {
        alert('항목 삭제에 실패했습니다.');
      }
    } catch (error) {
      // console.error('[LearningRecordsPanel] 폴더 아이템 삭제 오류:', error);
      alert('항목 삭제 중 오류가 발생했습니다.');
    }
  }, [folderDetailModal, isProfessor, effectiveBookUuid, removeFromSharedFolderUuids, handleFolderClick]);

  // 폴더 아이템 드래그 시작 핸들러
  const handleFolderItemDragStart = useCallback((e: React.DragEvent, itemNo: number, annotation: any) => {
    if (!isProfessor) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-folder-item', String(itemNo));
    folderItemDroppedInsideRef.current = false;
    setDraggedFolderItem({ itemNo, annotation });
  }, [isProfessor]);

  // 폴더 콘텐츠 영역 dragOver (모달 내부를 유효한 드롭 대상으로 유지 + 학습기록 드롭 허용)
  const handleFolderContentDragOver = useCallback((e: React.DragEvent) => {
    if (draggedFolderItem || draggedRecordUuid) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, [draggedFolderItem, draggedRecordUuid]);

  // 폴더 콘텐츠 영역 drop (폴더 아이템 드롭 시 삭제 방지 + 학습기록 드롭 시 폴더에 추가)
  const handleFolderContentDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();

    // 폴더 아이템을 모달 안에 드롭한 경우 (삭제 방지)
    if (draggedFolderItem) {
      folderItemDroppedInsideRef.current = true;
      return;
    }

    // 학습기록을 폴더 모달에 드롭한 경우 (폴더에 추가)
    if (draggedRecordUuid && folderDetailModal && effectiveBookUuid) {
      e.stopPropagation();
      const sourceRecordUuid = draggedRecordUuid;
      const folderUuid = folderDetailModal.folder.folder_uuid;

      try {
        const sourceRecord = recordsWithOriginalData.find(
          item => item.originalData.record_uuid === sourceRecordUuid
        );
        if (!sourceRecord) {
          // console.warn('[LearningRecordsPanel] 소스 카드를 찾을 수 없습니다.');
          return;
        }

        if (!isRecordTypeAllowedInSharedFolder(sourceRecord.originalData.record_type)) {
          alert('대화형 학습 기록은 공유 폴더에 담을 수 없습니다.');
          return;
        }

        const recordUuid = sourceRecord.originalData.record_uuid;
        const addResult = await sharedFolderService.addItemToFolder(folderUuid, recordUuid, 0, {});

        if (addResult.success) {
          addToSharedFolderUuids?.(effectiveBookUuid, recordUuid);
          setFolderItemCounts((prev) => ({
            ...prev,
            [folderUuid]: (prev[folderUuid] ?? 0) + 1,
          }));
          await handleFolderClick(folderDetailModal.folder);
        } else if (isDuplicateSharedFolderItemError(addResult.error || '')) {
          addToSharedFolderUuids?.(effectiveBookUuid, recordUuid);
          await handleFolderClick(folderDetailModal.folder);
        } else {
          alert(`폴더에 추가하는데 실패했습니다.${addResult.error ? `\n${addResult.error}` : ''}`);
        }
      } catch (err) {
        // console.error('[LearningRecordsPanel] 폴더 모달에 학습기록 드롭 실패:', err);
        alert(`폴더에 추가하는데 실패했습니다.${err instanceof Error ? `\n${err.message}` : ''}`);
      } finally {
        setDragOverTarget(null);
        setDraggedRecordUuid(null);
        setIsDraggingRecordOverModal(false);
        dragOverModalCountRef.current = 0;
      }
    }
  }, [draggedFolderItem, draggedRecordUuid, folderDetailModal, effectiveBookUuid, recordsWithOriginalData, addToSharedFolderUuids, handleFolderClick]);

  // 폴더 아이템 드래그 종료 핸들러 (영역 밖 드롭 시 삭제)
  const handleFolderItemDragEnd = useCallback(async () => {
    if (!draggedFolderItem || !folderDetailModal) {
      setDraggedFolderItem(null);
      return;
    }

    if (folderItemDroppedInsideRef.current) {
      setDraggedFolderItem(null);
      return;
    }

    try {
      const success = await sharedFolderService.deleteFolderItem(
        folderDetailModal.folder.folder_uuid,
        draggedFolderItem.itemNo
      );
      if (success) {
        const deletedAnnotation = draggedFolderItem.annotation;
        const recordUuid = deletedAnnotation?.record?.record_uuid || deletedAnnotation?.record_uuid;
        if (recordUuid) {
          removeFromSharedFolderUuids?.(effectiveBookUuid, recordUuid);
        }
        setFolderItemCounts((prev) => ({
          ...prev,
          [folderDetailModal.folder.folder_uuid]: Math.max(0, (prev[folderDetailModal.folder.folder_uuid] ?? 0) - 1),
        }));
        await handleFolderClick(folderDetailModal.folder);
      }
    } catch (error) {
      // console.error('[LearningRecordsPanel] 폴더 아이템 드래그 삭제 오류:', error);
    }

    setDraggedFolderItem(null);
  }, [draggedFolderItem, folderDetailModal, effectiveBookUuid, removeFromSharedFolderUuids, handleFolderClick]);

  // 공유 폴더 상세 모달 (학습 기록 패널과 독립적으로 표시)
  const renderFolderDetailModal = () => {
    if (!folderDetailModal) return null;
    const { folder, annotations, annotationVisible } = folderDetailModal;
    const bounds = folderModalBounds ?? {
      left: 492,
      top: 20,
      width: 450,
      height: 600,
    };
    return (
      <FolderDetailModalContent
        ref={folderDetailModalRef}
        $left={bounds.left}
        $top={bounds.top}
        $width={bounds.width}
        $height={bounds.height}
        $isDraggingItem={!!draggedFolderItem}
        $isDraggingRecordOver={isDraggingRecordOverModal}
        onDragOver={handleFolderContentDragOver}
        onDrop={handleFolderContentDrop}
        onDragEnter={(e) => {
          if (draggedRecordUuid) {
            e.preventDefault();
            dragOverModalCountRef.current++;
            setIsDraggingRecordOverModal(true);
          }
        }}
        onDragLeave={() => {
          if (draggedRecordUuid) {
            dragOverModalCountRef.current--;
            if (dragOverModalCountRef.current <= 0) {
              dragOverModalCountRef.current = 0;
              setIsDraggingRecordOverModal(false);
            }
          }
        }}
      >
          <PanelHeader>
            <PanelTitle>
              {folder.title}
              {!isProfessor && (
                <SharedFolderAnnotationToggleButton
                  $isVisible={annotationVisible}
                  onClick={handleToggleSharedFolderAnnotations}
                  aria-label={annotationVisible ? '공유 폴더 어노테이션 숨기기' : '공유 폴더 어노테이션 표시'}
                  title={`공유 폴더 어노테이션: ${annotationVisible ? '표시' : '숨김'}`}
                >
                  <ToggleBackground $isVisible={annotationVisible} />
                  <ToggleSlider $isVisible={annotationVisible} />
                </SharedFolderAnnotationToggleButton>
              )}
            </PanelTitle>
            <ToggleButton onClick={handleCloseFolderModal} aria-label="닫기">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 4L16 16" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M16 4L4 16" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </ToggleButton>
          </PanelHeader>
          {annotations.length > 0 && (
            <FolderDetailCountRow>
              {annotations.length}개의 학습기록
              <img src="/images/icons/learning-records.svg" alt="" width={14} height={14} />
            </FolderDetailCountRow>
          )}
          <PanelScrollContent>
          {loadingFolderAnnotations ? (
            <LoadingMessage>폴더 내용을 불러오는 중...</LoadingMessage>
          ) : annotations.length === 0 ? (
            <EmptyMessage>이 폴더에는 항목이 없습니다.</EmptyMessage>
          ) : (
            <RecordsList>
              {annotations.map((annotation, index) => {
                const recordType = annotation.record_type;
                const isNonAnnotation = recordType && recordType !== 'ANNOTATION';

                const annotationData = annotation.annotation || annotation;
                const data = annotationData.data || annotation.data || {};
                const annotationType = annotationData.type || annotation.type || '';
                const annotationApiTitle = annotation.annotationApiTitle || data.title || '';
                const summary = annotation.summary || data.summary || '';
                const annotationSubject = annotation.annotationApiSubject || data.subject || annotationData.data?.subject || '';

                let typeLabel = '마킹';
                let iconLabel = '마킹';
                let itemColor = '#f59e0b';
                let displayTitle = '';

                if (isNonAnnotation) {
                  switch (recordType) {
                    case 'AUDIO':
                      typeLabel = '녹음'; iconLabel = '녹음'; itemColor = '#DA0D0D';
                      break;
                    case 'NOTE':
                      typeLabel = '노트'; iconLabel = '노트'; itemColor = '#25E921';
                      break;
                    case 'DIALOG':
                    case 'QUIZ':
                    case 'CHAT':
                      typeLabel = 'AI'; iconLabel = 'AI'; itemColor = '#B10DDA';
                      break;
                    default:
                      typeLabel = '기타'; iconLabel = '기타'; itemColor = '#6b7280';
                      break;
                  }
                  displayTitle = annotation.title || `제목 없는 ${typeLabel}`;
                } else if (annotationType === 'MEMO') {
                  typeLabel = '메모';
                  iconLabel = '메모';
                  itemColor = '#091EBB';
                  displayTitle = annotationApiTitle || annotation.title || '';
                  displayTitle = displayTitle || `제목 없는 ${typeLabel}`;
                } else if (annotationType === 'UNDERLINE') {
                  typeLabel = '밑줄';
                  iconLabel = '밑줄';
                  itemColor = '#FB9701';
                  displayTitle = annotationApiTitle || annotation.title || '';
                  displayTitle = displayTitle || `제목 없는 ${typeLabel}`;
                } else if (annotationType === 'HIGHLIGHT') {
                  typeLabel = '강조';
                  iconLabel = '강조';
                  itemColor = '#FB9701';
                  displayTitle = annotationApiTitle || annotation.title || '';
                  displayTitle = displayTitle || `제목 없는 ${typeLabel}`;
                } else {
                  displayTitle = annotationApiTitle || annotation.title || '';
                  displayTitle = displayTitle || `제목 없는 ${typeLabel}`;
                }

                const pageDisplayNumber = annotation.page_display_number ?? annotationData.page_display_number ?? annotation.page_no;
                const pageIdx = annotation.page_idx ?? annotationData.page_idx ?? annotation.page_no;
                const isPageClickable = pageIdx != null && !!onPageClick;
                const annotationUuid = annotation.annotation_uuid || annotationData.annotation_uuid;
                const createdAt = annotation.created_at || annotationData.created_at;

                const itemKey = annotationUuid || annotation.record_uuid || `folder-item-${index}`;
                const isFolderItemExpanded = expandedFolderItemUuids.has(itemKey);
                const folderItemContent = annotationSubject || summary || '';

                const startOffset = annotationData.start_offset ?? data.start_offset ?? annotationData.range?.startOffset ?? annotationData.range?.start ?? data.range?.startOffset ?? data.range?.start;
                const endOffset = annotationData.end_offset ?? data.end_offset ?? annotationData.range?.endOffset ?? annotationData.range?.end ?? data.range?.endOffset ?? data.range?.end;
                const selectedText = annotationData.selected_text ?? data.selected_text ?? annotationData.range?.selectedText ?? data.range?.selectedText ?? '';
                const sharedAnnotationData = (startOffset !== undefined && endOffset !== undefined) ? {
                  startOffset: startOffset,
                  endOffset: endOffset,
                  selectedText: selectedText,
                  color: annotationData.data?.color || data.color || annotationData.color || '#4169E1',
                  type: annotationType,
                } : undefined;

                const handleItemClick = (e: React.MouseEvent) => {
                  if (isNonAnnotation && recordType === 'AUDIO') {
                    e.stopPropagation();
                    toggleFolderItemExpand(itemKey);
                  } else if (annotationType === 'MEMO') {
                    e.stopPropagation();
                    toggleFolderItemExpand(itemKey);
                  } else if (isPageClickable && pageIdx != null && onPageClick && annotationVisible) {
                    e.stopPropagation();
                    const memoFallback = annotationType === 'MEMO'
                      ? {
                          title: displayTitle || annotation.title || '',
                          subject: annotationSubject || annotation.subject || summary || ''
                        }
                      : undefined;
                    const componentNo = annotationData.component_no ?? undefined;
                    onPageClick(pageIdx, annotationUuid, memoFallback, componentNo, sharedAnnotationData);
                  }
                };

                const itemNo = annotation.item_no;
                const isFolderItemDragging = draggedFolderItem?.itemNo === itemNo;

                return (
                  <RecordItem
                    key={annotationUuid || annotation.record_uuid || index}
                    $color={itemColor}
                    $isDragging={isFolderItemDragging}
                    onClick={handleItemClick}
                    draggable={isProfessor}
                    onDragStart={(e) => isProfessor && handleFolderItemDragStart(e, itemNo, annotation)}
                    onDragEnd={handleFolderItemDragEnd}
                    style={{ cursor: isProfessor ? 'grab' : ((isPageClickable && annotationVisible) || annotationType === 'MEMO' || (isNonAnnotation && recordType === 'AUDIO')) ? 'pointer' : 'default' }}
                  >
                    <RecordHeader>
                      <RecordIcon>{iconLabel}</RecordIcon>
                      <RecordTitleRow>
                        <RecordTitle>{displayTitle}</RecordTitle>
                      </RecordTitleRow>
                    </RecordHeader>

                    {isNonAnnotation && summary ? (
                      <RecordContent $isExpanded={isFolderItemExpanded || summary.length <= MEMO_CONTENT_MAX_LEN}>
                        요약 : {isFolderItemExpanded || summary.length <= MEMO_CONTENT_MAX_LEN
                          ? summary
                          : truncateWithEllipsis(summary, MEMO_CONTENT_MAX_LEN)}
                      </RecordContent>
                    ) : annotationType === 'MEMO' && folderItemContent ? (
                      <RecordContent $isExpanded={isFolderItemExpanded || folderItemContent.length <= MEMO_CONTENT_MAX_LEN}>
                        {isFolderItemExpanded || folderItemContent.length <= MEMO_CONTENT_MAX_LEN
                          ? folderItemContent
                          : truncateWithEllipsis(folderItemContent, MEMO_CONTENT_MAX_LEN)}
                      </RecordContent>
                    ) : annotationSubject ? (
                      <RecordContent>{annotationSubject}</RecordContent>
                    ) : summary ? (
                      <RecordContent>요약 : {summary}</RecordContent>
                    ) : null}

                    {isNonAnnotation && recordType === 'AUDIO' && annotation.data?.file_uuid && isFolderItemExpanded && (
                      <AudioPlayerItem
                        recordUuid={annotation.record_uuid || itemKey}
                        fileUuid={annotation.data.file_uuid}
                        playingAudioUuid={playingAudioUuid}
                        setPlayingAudioUuid={setPlayingAudioUuid}
                        audioRefs={audioRefs}
                      />
                    )}

                    <RecordMeta>
                      {createdAt && <RecordDate>{formatDate(createdAt)}</RecordDate>}
                      {createdAt && pageDisplayNumber != null && (
                        <MetaSeparator>|</MetaSeparator>
                      )}
                      {pageDisplayNumber != null && (
                        <PageNumber onClick={(e) => {
                          e.stopPropagation();
                          if (isPageClickable && pageIdx != null) {
                            const memoFallback = annotationType === 'MEMO'
                              ? { title: displayTitle || annotation.title || '', subject: annotationSubject || annotation.subject || summary || '' }
                              : undefined;
                            const componentNo = annotationData.component_no ?? undefined;
                            onPageClick(pageIdx, annotationUuid, memoFallback, componentNo, sharedAnnotationData);
                          }
                        }} style={{ cursor: isPageClickable ? 'pointer' : 'default', textDecoration: isPageClickable ? 'underline' : 'none' }}>
                          P.{pageDisplayNumber ?? 0}
                        </PageNumber>
                      )}
                      
                    </RecordMeta>
                    <RecordMetaRight>
                      {(annotationType === 'MEMO' || (isNonAnnotation && recordType === 'AUDIO')) && pageIdx != null && onPageClick && annotationVisible && (
                        <GotoLocationButton
                          onClick={(e) => {
                            e.stopPropagation();
                            const memoFallback = annotationType === 'MEMO'
                              ? {
                                  title: displayTitle || annotation.title || '',
                                  subject: annotationSubject || annotation.subject || summary || ''
                                }
                              : undefined;
                            const componentNo = annotationData.component_no ?? undefined;
                            onPageClick(pageIdx, annotationUuid, memoFallback, componentNo);
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                          위치로 이동
                        </GotoLocationButton>
                      )}
                      {isProfessor && itemNo !== undefined && (
                          <DeleteButton onClick={(e) => { e.stopPropagation(); handleDeleteFolderItem(itemNo); }} aria-label="항목 삭제">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path fillRule="evenodd" clipRule="evenodd" d="M2.985 4.52273C3.10071 4.51505 3.21474 4.55362 3.30202 4.62997C3.38931 4.70631 3.44271 4.81419 3.4505 4.9299L3.71883 8.9549C3.77133 9.74065 3.80866 10.2878 3.89033 10.6991C3.97025 11.0986 4.08108 11.3098 4.24033 11.4591C4.40016 11.6085 4.61833 11.7053 5.022 11.7578C5.4385 11.8121 5.98683 11.8132 6.77433 11.8132H7.22583C8.01333 11.8132 8.56167 11.8121 8.97817 11.7578C9.38183 11.7053 9.6 11.6085 9.75983 11.4591C9.91908 11.3098 10.0299 11.0986 10.1098 10.6996C10.1915 10.2878 10.2288 9.74065 10.2813 8.95431L10.5497 4.9299C10.5535 4.87256 10.5686 4.81654 10.5941 4.76503C10.6195 4.71352 10.6549 4.66754 10.6982 4.6297C10.7414 4.59186 10.7917 4.56292 10.8461 4.54451C10.9006 4.52611 10.9581 4.51861 11.0155 4.52244C11.0728 4.52627 11.1288 4.54136 11.1803 4.56684C11.2318 4.59232 11.2778 4.6277 11.3157 4.67095C11.3535 4.7142 11.3824 4.76448 11.4008 4.81892C11.4192 4.87336 11.4267 4.93089 11.4229 4.98823L11.1522 9.04356C11.1027 9.7914 11.0624 10.3963 10.9679 10.8706C10.8693 11.3635 10.7025 11.7753 10.3572 12.0979C10.0118 12.4205 9.59008 12.5605 9.09133 12.6252C8.61183 12.6882 8.00633 12.6882 7.25675 12.6882H6.74342C5.99383 12.6882 5.38833 12.6882 4.90883 12.6252C4.41008 12.5605 3.98775 12.4211 3.643 12.0979C3.29766 11.7753 3.13083 11.3629 3.03225 10.8706C2.93775 10.3957 2.89808 9.7914 2.84791 9.04356L2.57725 4.98823C2.57345 4.93089 2.58098 4.87336 2.59941 4.81893C2.61785 4.7645 2.64683 4.71424 2.68469 4.67101C2.72256 4.62778 2.76857 4.59244 2.82009 4.567C2.87162 4.54155 2.92766 4.52651 2.985 4.52273ZM6.0405 1.31323H6.01367C5.88767 1.31323 5.77741 1.31323 5.67358 1.32956C5.47133 1.36196 5.27947 1.44125 5.11336 1.56108C4.94724 1.68091 4.81147 1.83795 4.71692 2.01965C4.66792 2.11298 4.6335 2.2174 4.59383 2.33698L4.58508 2.36265L4.5285 2.53181L4.51158 2.58198C4.45877 2.72859 4.36038 2.85442 4.23083 2.94102C4.10129 3.02763 3.94741 3.07046 3.79175 3.06323H2.04175C1.92572 3.06323 1.81444 3.10932 1.73239 3.19137C1.65034 3.27342 1.60425 3.3847 1.60425 3.50073C1.60425 3.61676 1.65034 3.72804 1.73239 3.81009C1.81444 3.89214 1.92572 3.93823 2.04175 3.93823H11.959C12.075 3.93823 12.1863 3.89214 12.2684 3.81009C12.3504 3.72804 12.3965 3.61676 12.3965 3.50073C12.3965 3.3847 12.3504 3.27342 12.2684 3.19137C12.1863 3.10932 12.075 3.06323 11.959 3.06323H10.1559C10.0001 3.05949 9.84959 3.00593 9.72645 2.9104C9.60331 2.81487 9.51401 2.68239 9.47166 2.5324L9.41508 2.36265L9.40691 2.33698C9.36666 2.2174 9.33225 2.11298 9.28383 2.01965C9.18922 1.83788 9.05336 1.68079 8.88714 1.56096C8.72091 1.44113 8.52893 1.36188 8.32658 1.32956C8.21377 1.31585 8.10011 1.31039 7.9865 1.31323H6.0405ZM5.33467 2.87948C5.31133 2.94326 5.28469 3.00451 5.25475 3.06323H8.74541C8.71551 3.00373 8.68902 2.94258 8.66608 2.88006L8.64275 2.81356L8.585 2.63856C8.56384 2.5654 8.53792 2.49369 8.50741 2.4239C8.47592 2.36324 8.43066 2.3108 8.37525 2.27078C8.31984 2.23077 8.25582 2.20429 8.18833 2.19348C8.11231 2.18675 8.03592 2.18499 7.95967 2.18823H6.0405C5.8725 2.18823 5.8375 2.1894 5.81183 2.19406C5.7444 2.20482 5.68042 2.2312 5.62501 2.27111C5.56961 2.31102 5.52431 2.36334 5.49275 2.4239C5.48108 2.44723 5.46825 2.47931 5.41516 2.63915L5.35683 2.81415L5.33467 2.87948Z" fill="#4B4B4B"/>
                            </svg>
                            삭제
                          </DeleteButton>
                        )}
                    </RecordMetaRight>
                  </RecordItem>
                );
              })}
            </RecordsList>
          )}
          </PanelScrollContent>
          {draggedFolderItem && (
            <FolderItemDragHint>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="#ef4444" strokeLinecap="round" strokeWidth="1.5" />
              </svg>
              영역 밖으로 드래그하여 삭제
            </FolderItemDragHint>
          )}
      </FolderDetailModalContent>
    );
  };

  // 학습 기록 목록 뷰 (항상 표시, 폴더/공유받은폴더 클릭 시 별도 모달로 상세 표시)
  return (
    <>
      {renderFolderDetailModal()}
      <Panel ref={panelContainerRef}>
        <PanelHeader>
          <PanelTitle>학습 기록</PanelTitle>
          <ToggleButton onClick={onToggle} aria-label="접기">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M16 4L4 16" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </ToggleButton>
        </PanelHeader>

        <PanelScrollContent ref={panelRef} onScroll={handleScroll}>
      {/* 학생: 공유받은 폴더 섹션 */}
      {isStudent && receivedSharedFolder && (
        <ReceivedFolderSection>
          <ReceivedFolderTitle>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            공유받은 폴더
          </ReceivedFolderTitle>
          <ReceivedFolderGrid>
          <ReceivedFolderItem
            onClick={async () => {
              if (receivedSharedFolder.folder_uuid) {
                const folderData: SharedFolderData = {
                  folder_uuid: receivedSharedFolder.folder_uuid,
                  book_uuid: receivedSharedFolder.book_uuid,
                  owner_no: 0,
                  title: receivedSharedFolder.title,
                  description: '',
                  meta: {},
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  content_updated_at: new Date().toISOString(),
                  is_new: true,
                  changed_at: new Date().toISOString(),
                };
                await handleFolderClick(folderData);
              }
            }}
          >
            <RecordHeader>
              <FolderIcon></FolderIcon>
              <RecordTitleRow>
                <FolderTitle>{receivedSharedFolder.title}</FolderTitle>
                <UpdateBadge>UPDATE</UpdateBadge>
              </RecordTitleRow>
            </RecordHeader>
          </ReceivedFolderItem>
          </ReceivedFolderGrid>
        </ReceivedFolderSection>
      )}

      {loading ? (
        <LoadingMessage>학습 기록을 불러오는 중...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : records.length === 0 && folders.length === 0 && !receivedSharedFolder && !pendingRecordingCard ? (
        <EmptyMessage>학습 기록이 없습니다.</EmptyMessage>
      ) : (
        <RecordsList>
          {pendingRecordingCard && (
            <RecordItem
              key="pending-recording"
              $color="#3b82f6"
              style={{ cursor: 'pointer', opacity: 0.9 }}
              onClick={() => togglePendingAudio()}
            >
              <RecordHeader>
                <RecordIcon $color="#3b82f6">녹음</RecordIcon>
                <RecordTitleRow>
                  <RecordTitle>{pendingRecordingCard.title}</RecordTitle>
                </RecordTitleRow>
              </RecordHeader>

              <RecordContent>요약 : 생성중입니다...</RecordContent>

              <AudioPlayerContainer>
                <PlayButton onClick={(e) => { e.stopPropagation(); togglePendingAudio(); }}>
                  {pendingAudioPlaying ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M10.416 7.45096C11.2222 8.10871 11.625 8.43721 11.625 8.99971C11.625 9.56221 11.2222 9.89071 10.416 10.5485C10.1932 10.73 9.97275 10.901 9.7695 11.0435C9.59175 11.1687 9.39 11.2977 9.1815 11.4245C8.37675 11.9135 7.97475 12.158 7.614 11.8872C7.254 11.6172 7.221 11.0502 7.155 9.91621C7.137 9.59596 7.125 9.28171 7.125 8.99971C7.125 8.71771 7.137 8.40346 7.155 8.08321C7.221 6.94921 7.254 6.38296 7.614 6.11221C7.97475 5.84146 8.3775 6.08596 9.1815 6.57496C9.39 6.70171 9.59175 6.83071 9.7695 6.95596C9.972 7.09846 10.1932 7.26946 10.416 7.45096Z" stroke="#4B4B4B" strokeWidth="1.13"/>
                      <path d="M5.25 2.50351C6.38959 1.84405 7.68336 1.49784 9 1.50001C13.1422 1.50001 16.5 4.85776 16.5 9.00001C16.5 13.1423 13.1422 16.5 9 16.5C4.85775 16.5 1.5 13.1423 1.5 9.00001C1.5 7.63426 1.86525 6.35251 2.5035 5.25001" stroke="#4B4B4B" strokeWidth="1.13" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path d="M11.9168 6.50016C11.9168 9.05358 11.9168 10.3303 11.1233 11.1233C10.3308 11.9168 9.05358 11.9168 6.50016 11.9168C3.94675 11.9168 2.67004 11.9168 1.8765 11.1233C1.0835 10.3308 1.0835 9.05358 1.0835 6.50016C1.0835 3.94675 1.0835 2.67004 1.8765 1.8765C2.67058 1.0835 3.94675 1.0835 6.50016 1.0835C9.05358 1.0835 10.3303 1.0835 11.1233 1.8765C11.6509 2.40408 11.8275 3.14562 11.887 4.3335" stroke="#EBEBEB" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                </PlayButton>
                <AudioPlayer
                  src={pendingRecordingCard.audioBlobUrl}
                  controls
                  preload="metadata"
                />
              </AudioPlayerContainer>

              <RecordMeta>
                <RecordDate>{formatDate(pendingRecordingCard.createdAt)}</RecordDate>
              </RecordMeta>
            </RecordItem>
          )}
          {mergedItems.map((item) => {
          if (item.type === 'folder') {
            const folder = item.folder;
            const isDragOver = dragOverTarget?.type === 'folder' && dragOverTarget.uuid === folder.folder_uuid;
            return (
              <FolderItem
                key={`folder-${folder.folder_uuid}`}
                $isDragOver={isDragOver}
                onClick={() => handleFolderClick(folder)}
                onDragOver={(e) => isProfessor && handleDragOverFolder(e, folder.folder_uuid)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => isProfessor && handleDropOnFolder(e, folder.folder_uuid)}
              >
                <RecordHeader>
                  <FolderIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M13.5 7.5H9.75M16.5 8.8485C16.5 6.8745 16.5 5.88675 15.9225 5.2455C15.8695 5.18632 15.8134 5.13 15.7545 5.07675C15.1133 4.5 14.1255 4.5 12.1515 4.5H11.871C11.0063 4.5 10.5735 4.5 10.17 4.38525C9.94857 4.32195 9.73516 4.23337 9.534 4.12125C9.168 3.918 8.862 3.61125 8.25 3L7.8375 2.5875C7.632 2.382 7.53 2.28 7.422 2.19C6.9575 1.80498 6.3877 1.56896 5.787 1.51275C5.6475 1.5 5.502 1.5 5.2125 1.5C4.55025 1.5 4.2195 1.5 3.9435 1.5525C3.35088 1.66442 2.80575 1.95235 2.37923 2.37874C1.95271 2.80512 1.6646 3.35016 1.5525 3.94275C1.5 4.2195 1.5 4.551 1.5 5.2125M16.4932 12C16.4662 13.86 16.3282 14.9137 15.621 15.621C14.7427 16.5 13.3282 16.5 10.5 16.5H7.5C4.67175 16.5 3.25725 16.5 2.379 15.621C1.50075 14.742 1.5 13.3282 1.5 10.5V8.25" stroke="#3B3B3B" strokeWidth="1.35" strokeLinecap="round"/>
                    </svg>
                  </FolderIcon>
                  <RecordTitleRow>
                    <FolderTitle as="span">{folder.title}</FolderTitle>
                  </RecordTitleRow>
                </RecordHeader>
                <FolderItemCountRow>
                  {folderItemCounts[folder.folder_uuid] ?? 0}개의 학습기록
                </FolderItemCountRow>
                {(folder.created_at || folder.is_new) && (
                  <RecordMeta>
                    {folder.created_at && <RecordDate>{formatDate(folder.created_at)}</RecordDate>}
                    {folder.is_new && <UpdateBadge>UPDATE</UpdateBadge>}
                  </RecordMeta>
                )}
                {isProfessor && (
                  <FolderActions>
                    <SendButton
                      $disabled={!isSyncSocketConnected}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSyncSocketConnected && sendSharedFolderPublish) {
                          sendSharedFolderPublish(folder.folder_uuid, effectiveBookUuid, folder.title);
                          alert('공유 폴더를 학생들에게 전송했습니다.');
                        } else {
                          alert('동기화 연결이 필요합니다. 먼저 동기화를 시작해주세요.');
                        }
                      }}
                      title={isSyncSocketConnected ? '학생들에게 전송' : '동기화 연결 필요'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.5839 2.11834C9.9014 2.24026 8.9809 2.54593 7.68999 2.97584L4.76574 3.95001C3.7274 4.29651 2.97549 4.54793 2.44407 4.77368C1.88874 5.00993 1.69157 5.17443 1.6274 5.28468C1.5305 5.45181 1.47947 5.64157 1.47947 5.83476C1.47947 6.02795 1.5305 6.21771 1.6274 6.38484C1.69157 6.49509 1.88874 6.65959 2.44407 6.89584C2.97549 7.12159 3.7274 7.37301 4.76574 7.71893L4.81474 7.73526C5.09824 7.82976 5.31874 7.90268 5.50832 8.02984C5.6909 8.15234 5.84782 8.30926 5.97032 8.49184C6.09749 8.68143 6.1704 8.90193 6.2649 9.18601L6.28124 9.23443C6.62715 10.2728 6.87857 11.0247 7.10374 11.5561C7.34057 12.1114 7.50507 12.3086 7.61532 12.3728C7.78245 12.4697 7.97221 12.5207 8.16541 12.5207C8.3586 12.5207 8.54836 12.4697 8.71549 12.3728C8.82574 12.3086 8.99024 12.1114 9.22649 11.5561C9.45224 11.0247 9.70366 10.2728 10.0496 9.23443L11.0237 6.31076C11.4542 5.01984 11.7599 4.09993 11.8812 3.41743C12.0037 2.73143 11.9104 2.43976 11.7354 2.26476C11.5604 2.08976 11.2687 1.99643 10.5833 2.11893M10.4258 1.23693C11.1859 1.10159 11.8702 1.13251 12.3689 1.63184C12.8682 2.13059 12.8992 2.81484 12.7638 3.57434C12.6297 4.32976 12.3018 5.31268 11.8871 6.55634L10.8925 9.54009C10.5553 10.5522 10.2934 11.3385 10.0513 11.9073C9.81799 12.4556 9.55899 12.9199 9.16582 13.1486C8.86205 13.3254 8.51686 13.4185 8.16541 13.4185C7.81395 13.4185 7.46876 13.3254 7.16499 13.1486C6.77182 12.9199 6.51282 12.4556 6.27949 11.9073C6.03741 11.3385 5.77549 10.5522 5.43774 9.54009L5.43074 9.51793C5.31232 9.16384 5.27674 9.06759 5.2254 8.99118C5.16766 8.90631 5.09444 8.83308 5.00957 8.77534C4.93374 8.72401 4.8369 8.68784 4.48224 8.57001L4.46065 8.56301C3.44857 8.22526 2.66224 7.96334 2.09349 7.72126C1.54515 7.48793 1.08082 7.22893 0.852154 6.83576C0.675394 6.53199 0.582275 6.1868 0.582275 5.83534C0.582275 5.48389 0.675394 5.1387 0.852154 4.83493C1.08082 4.44176 1.54515 4.18276 2.09349 3.94943C2.66224 3.70734 3.44857 3.44543 4.46124 3.10826L7.4444 2.11368C8.68866 1.69893 9.67099 1.37109 10.4264 1.23693M10.3138 3.69101C10.3972 3.77566 10.4437 3.88993 10.4431 4.00878C10.4424 4.12762 10.3947 4.24137 10.3103 4.32509L7.91982 6.68876C7.83498 6.77043 7.72138 6.81541 7.60363 6.81396C7.48587 6.81252 7.37341 6.76476 7.2906 6.68103C7.20779 6.59729 7.16128 6.48432 7.16113 6.36655C7.16099 6.24879 7.20722 6.1357 7.28982 6.05176L9.67974 3.68751C9.76439 3.60408 9.87865 3.55761 9.9975 3.55827C10.1164 3.55892 10.2301 3.60666 10.3138 3.69101Z" fill="#4B4B4B"/>
                      </svg>
                      노트 전송
                    </SendButton>
                    <DeleteButton
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`"${folder.title}" 폴더를 삭제하시겠습니까?`)) {
                          return;
                        }

                        try {
                          const success = await sharedFolderService.deleteSharedFolder(folder.folder_uuid);
                          if (success) {
                            invalidate?.(effectiveBookUuid);
                            alert('폴더가 삭제되었습니다.');
                          } else {
                            alert('폴더 삭제에 실패했습니다.');
                          }
                        } catch (error) {
                          // console.error('[LearningRecordsPanel] 폴더 삭제 오류:', error);
                          alert('폴더 삭제 중 오류가 발생했습니다.');
                        }
                      }}
                      aria-label="폴더 삭제"
                      title="폴더 삭제"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M2.985 4.52273C3.10071 4.51505 3.21474 4.55362 3.30202 4.62997C3.38931 4.70631 3.44271 4.81419 3.4505 4.9299L3.71883 8.9549C3.77133 9.74065 3.80866 10.2878 3.89033 10.6991C3.97025 11.0986 4.08108 11.3098 4.24033 11.4591C4.40016 11.6085 4.61833 11.7053 5.022 11.7578C5.4385 11.8121 5.98683 11.8132 6.77433 11.8132H7.22583C8.01333 11.8132 8.56167 11.8121 8.97817 11.7578C9.38183 11.7053 9.6 11.6085 9.75983 11.4591C9.91908 11.3098 10.0299 11.0986 10.1098 10.6996C10.1915 10.2878 10.2288 9.74065 10.2813 8.95431L10.5497 4.9299C10.5535 4.87256 10.5686 4.81654 10.5941 4.76503C10.6195 4.71352 10.6549 4.66754 10.6982 4.6297C10.7414 4.59186 10.7917 4.56292 10.8461 4.54451C10.9006 4.52611 10.9581 4.51861 11.0155 4.52244C11.0728 4.52627 11.1288 4.54136 11.1803 4.56684C11.2318 4.59232 11.2778 4.6277 11.3157 4.67095C11.3535 4.7142 11.3824 4.76448 11.4008 4.81892C11.4192 4.87336 11.4267 4.93089 11.4229 4.98823L11.1522 9.04356C11.1027 9.7914 11.0624 10.3963 10.9679 10.8706C10.8693 11.3635 10.7025 11.7753 10.3572 12.0979C10.0118 12.4205 9.59008 12.5605 9.09133 12.6252C8.61183 12.6882 8.00633 12.6882 7.25675 12.6882H6.74342C5.99383 12.6882 5.38833 12.6882 4.90883 12.6252C4.41008 12.5605 3.98775 12.4211 3.643 12.0979C3.29766 11.7753 3.13083 11.3629 3.03225 10.8706C2.93775 10.3957 2.89808 9.7914 2.84791 9.04356L2.57725 4.98823C2.57345 4.93089 2.58098 4.87336 2.59941 4.81893C2.61785 4.7645 2.64683 4.71424 2.68469 4.67101C2.72256 4.62778 2.76857 4.59244 2.82009 4.567C2.87162 4.54155 2.92766 4.52651 2.985 4.52273ZM6.0405 1.31323H6.01367C5.88767 1.31323 5.77741 1.31323 5.67358 1.32956C5.47133 1.36196 5.27947 1.44125 5.11336 1.56108C4.94724 1.68091 4.81147 1.83795 4.71692 2.01965C4.66792 2.11298 4.6335 2.2174 4.59383 2.33698L4.58508 2.36265L4.5285 2.53181L4.51158 2.58198C4.45877 2.72859 4.36038 2.85442 4.23083 2.94102C4.10129 3.02763 3.94741 3.07046 3.79175 3.06323H2.04175C1.92572 3.06323 1.81444 3.10932 1.73239 3.19137C1.65034 3.27342 1.60425 3.3847 1.60425 3.50073C1.60425 3.61676 1.65034 3.72804 1.73239 3.81009C1.81444 3.89214 1.92572 3.93823 2.04175 3.93823H11.959C12.075 3.93823 12.1863 3.89214 12.2684 3.81009C12.3504 3.72804 12.3965 3.61676 12.3965 3.50073C12.3965 3.3847 12.3504 3.27342 12.2684 3.19137C12.1863 3.10932 12.075 3.06323 11.959 3.06323H10.1559C10.0001 3.05949 9.84959 3.00593 9.72645 2.9104C9.60331 2.81487 9.51401 2.68239 9.47166 2.5324L9.41508 2.36265L9.40691 2.33698C9.36666 2.2174 9.33225 2.11298 9.28383 2.01965C9.18922 1.83788 9.05336 1.68079 8.88714 1.56096C8.72091 1.44113 8.52893 1.36188 8.32658 1.32956C8.21377 1.31585 8.10011 1.31039 7.9865 1.31323H6.0405ZM5.33467 2.87948C5.31133 2.94326 5.28469 3.00451 5.25475 3.06323H8.74541C8.71551 3.00373 8.68902 2.94258 8.66608 2.88006L8.64275 2.81356L8.585 2.63856C8.56384 2.5654 8.53792 2.49369 8.50741 2.4239C8.47592 2.36324 8.43066 2.3108 8.37525 2.27078C8.31984 2.23077 8.25582 2.20429 8.18833 2.19348C8.11231 2.18675 8.03592 2.18499 7.95967 2.18823H6.0405C5.8725 2.18823 5.8375 2.1894 5.81183 2.19406C5.7444 2.20482 5.68042 2.2312 5.62501 2.27111C5.56961 2.31102 5.52431 2.36334 5.49275 2.4239C5.48108 2.44723 5.46825 2.47931 5.41516 2.63915L5.35683 2.81415L5.33467 2.87948Z" fill="#4B4B4B"/>
                      </svg>
                      삭제
                    </DeleteButton>
                  </FolderActions>
                )}
              </FolderItem>
            );
          }
          const { record, originalData, index } = item;
            const isDialogClickable = record.recordType === 'DIALOG' && onRecordClick;
            const isPageClickable = (record.pageIdx !== null && record.pageIdx !== undefined) && onPageClick;
            const isClickable = isDialogClickable || isPageClickable;
            const recordUuid = originalData?.record_uuid || '';
            const isDragging = draggedRecordUuid === recordUuid;
            const isDragOver = dragOverTarget?.type === 'record' && dragOverTarget.uuid === recordUuid;
            
            return (
            <RecordItem
              key={`record-${originalData?.record_uuid ?? index}`} 
              $color={record.color}
              $isDragging={isDragging}
              $isDragOver={isDragOver}
              draggable={isProfessor && isRecordTypeAllowedInSharedFolder(record.recordType)}
              title={isProfessor && record.recordType === 'DIALOG' ? '대화형 학습 기록은 공유 폴더에 담을 수 없습니다' : undefined}
              onDragStart={(e) => isProfessor && handleDragStart(e, recordUuid)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => {
                if (isProfessor) {
                  handleDragOverRecord(e, recordUuid);
                }
              }}
              onDragLeave={handleDragLeave}
              onDrop={(e) => isProfessor && handleDropOnRecord(e, recordUuid)}
              onClick={async () => {
                const isMemo = record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO';
                const isAudio = record.recordType === 'AUDIO';
                if (isMemo || isAudio) {
                  toggleRecordExpand(recordUuid);
                } else if (isDialogClickable && originalData) {
                  onRecordClick(originalData);
                } else if (isPageClickable && record.pageIdx !== null) {
                  const annotationUuid = record.recordType === 'ANNOTATION' && originalData?.annotation_uuid
                    ? originalData.annotation_uuid
                    : undefined;
                  const memoFallback = record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO'
                    ? {
                        title: record.title || originalData?.title || '',
                        subject: record.subject || record.summary || originalData?.summary || ''
                      }
                    : undefined;
                  const componentNo = originalData?.data?.component_no ?? undefined;
                  onPageClick(record.pageIdx, annotationUuid, memoFallback, componentNo);
                }
              }}
              style={{ cursor: (isClickable || (record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO') || record.recordType === 'AUDIO') ? 'pointer' : (isProfessor ? 'grab' : 'default') }}
            >
              <RecordHeader>
                <RecordIcon $color={record.color}>
                  {record.iconLabel}
                </RecordIcon>
                <RecordTitleRow>
                  <RecordTitle>{record.title}</RecordTitle>
                </RecordTitleRow>
              </RecordHeader>

              {record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO' ? (
                (() => {
                  const content = record.subject || record.summary || '';
                  const isExpanded = expandedRecordUuids.has(recordUuid);
                  const displayContent = isExpanded || content.length <= MEMO_CONTENT_MAX_LEN
                    ? content
                    : truncateWithEllipsis(content, MEMO_CONTENT_MAX_LEN);
                  return (
                    <RecordContent $isExpanded={isExpanded || content.length <= MEMO_CONTENT_MAX_LEN}>
                      {displayContent}
                    </RecordContent>
                  );
                })()
              ) : record.tags.length > 0 ? (
                <RecordContent>
                  {record.tags.map((tag) => `${tag.label} : ${tag.content}`).join(' ')}
                </RecordContent>
              ) : !record.tags.length && record.subject && record.recordType !== 'AUDIO' ? (
                <RecordContent>{record.subject}</RecordContent>
              ) : !record.tags.length && !record.subject && record.summary && record.recordType !== 'AUDIO' ? (
                <RecordContent>요약 : {record.summary}</RecordContent>
              ) : record.recordType === 'AUDIO' && !expandedRecordUuids.has(recordUuid) ? (
                <RecordContent>요약 : {truncateWithEllipsis(record.summary || record.subject || '', MEMO_CONTENT_MAX_LEN)}</RecordContent>
              ) : null}

              {record.recordType === 'AUDIO' && originalData?.data?.file_uuid && expandedRecordUuids.has(recordUuid) && (
                <AudioPlayerItem 
                  recordUuid={recordUuid}
                  fileUuid={originalData.data.file_uuid}
                  playingAudioUuid={playingAudioUuid}
                  setPlayingAudioUuid={setPlayingAudioUuid}
                  audioRefs={audioRefs}
                />
              )}

              <RecordMeta>
                {record.date && <RecordDate>{record.date}</RecordDate>}
                {record.date && record.pageDisplayNumber !== null && record.pageDisplayNumber !== undefined && (
                  <MetaSeparator>|</MetaSeparator>
                )}
                {record.pageDisplayNumber !== null && record.pageDisplayNumber !== undefined ? (
                  <PageNumber 
                    data-page-display-number={record.pageDisplayNumber}
                    data-page-idx={record.pageIdx}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPageClickable && record.pageIdx !== null) {
                        // console.log('[학습 기록 패널] 페이지 클릭:', {
                        //   pageDisplayNumber: record.pageDisplayNumber,
                        //   pageIdx: record.pageIdx,
                        //   annotationUuid: record.recordType === 'ANNOTATION' && originalData?.annotation_uuid
                        //     ? originalData.annotation_uuid
                        //     : undefined
                        // });
                        const annotationUuid = record.recordType === 'ANNOTATION' && originalData?.annotation_uuid
                          ? originalData.annotation_uuid
                          : undefined;
                        const memoFallback = record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO'
                          ? {
                              title: record.title || originalData?.title || '',
                              subject: record.subject || record.summary || originalData?.summary || ''
                            }
                          : undefined;
                        const componentNo = originalData?.data?.component_no ?? undefined;
                        onPageClick(record.pageIdx, annotationUuid, memoFallback, componentNo);
                      }
                    }}
                    style={{ cursor: isPageClickable ? 'pointer' : 'default', textDecoration: isPageClickable ? 'underline' : 'none' }}
                  >
                    P.{record.pageDisplayNumber}
                  </PageNumber>
                ) : null}
              </RecordMeta>
              {((record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO') || record.recordType === 'AUDIO') && record.pageIdx != null && onPageClick && (
                <GotoLocationButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (record.pageIdx == null) return;
                    const annotationUuid = record.recordType === 'ANNOTATION' && originalData?.annotation_uuid
                      ? originalData.annotation_uuid
                      : undefined;
                    const memoFallback = record.recordType === 'ANNOTATION' && record.annotationType === 'MEMO'
                      ? {
                          title: record.title || originalData?.title || '',
                          subject: record.subject || record.summary || originalData?.summary || ''
                        }
                      : undefined;
                    const componentNo = originalData?.data?.component_no ?? undefined;
                    onPageClick(record.pageIdx, annotationUuid, memoFallback, componentNo);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  위치로 이동
                </GotoLocationButton>
              )}
            </RecordItem>
            );
          })}
          {loadingMore && (
            <LoadingMessage>추가 학습 기록을 불러오는 중...</LoadingMessage>
          )}
          {!hasMore && records.length > 0 && (
            <EmptyMessage>모든 학습 기록을 불러왔습니다.</EmptyMessage>
          )}
        </RecordsList>
      )}
        </PanelScrollContent>

      </Panel>
    </>
  );
};

export default LearningRecordsPanel;

